import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  Input,
  Radio,
  message,
  Button,
  Select,
  Modal,
  List,
  Space,
  Upload,
  Spin,
} from "antd";
import { UploadOutlined, SearchOutlined } from "@ant-design/icons";
import { Locale, ApiManager, Util } from "../../utils";
import { connect } from "react-redux";
import { getUserInfo } from "../../utils/redux/reducers/User";
import {
  bankListTitle,
  bankMatchesSearchKeyword,
  mapBankCardOcr,
  unwrapBankingList,
} from "../../utils/helpers/wechatApplymentHelpers";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();
const util = Util.getInstance();

function AccountInfoSettingForm({
  user,
  organizationType = "4",
  contactType = "65",
}) {
  const [form] = Form.useForm();
  const refData = useRef();
  const isAgentContact = organizationType === "4" && contactType === "66";

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [provinceCode, setProvinceCode] = useState(null);
  const [cityCode, setCityCode] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [bankKeyword, setBankKeyword] = useState("");
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);

  const bankAccountType = Form.useWatch("bank_account_type", form) || "74";

  const filteredBanks = useMemo(
    () => banks.filter((b) => bankMatchesSearchKeyword(b, bankKeyword)),
    [banks, bankKeyword]
  );

  const getData = useCallback(async () => {
    const { token } = user;
    if (!token) return;
    try {
      const res = await api.merchant.getMerchantAccountInfo.run({ token });
      if (res.result) {
        const {
          id,
          contact_name,
          contact_phone,
          bank_name,
          bank_account,
          bank_account_name,
          bank_account_type,
          account_bank,
          bank_branch_id,
          bank_address_code,
        } = res.data;
        refData.current = res.data;
        form.setFieldsValue({
          id,
          contact_name,
          contact_phone,
          bank_name,
          bank_account,
          bank_account_name,
          bank_account_type,
          account_bank,
          bank_address_code,
          bank_branch_id,
        });
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    }
  }, [user, form]);

  const loadProvinces = useCallback(async () => {
    const { token } = user;
    if (!token) return;
    try {
      const res = await api.merchant.bankingProvinces.run({ token });
      if (res.result) {
        setProvinces(unwrapBankingList(res.data));
      }
    } catch (e) {
      /* optional assist */
    }
  }, [user]);

  useEffect(() => {
    getData();
    loadProvinces();
  }, [getData, loadProvinces]);

  const loadCities = async (code) => {
    const { token } = user;
    if (!token || !code) return;
    try {
      const res = await api.merchant.bankingCities.run({
        token,
        query: { province_code: code },
      });
      if (res.result) {
        setCities(unwrapBankingList(res.data));
      }
    } catch (e) {
      message.warning(e.message);
    }
  };

  const loadAllBanks = async (accountType) => {
    const { token } = user;
    if (!token) return;
    setLoadingBanks(true);
    try {
      const isCorp = String(accountType) === "74";
      const endpoint = isCorp
        ? api.merchant.bankingCorporateBanks
        : api.merchant.bankingPersonalBanks;
      let offset = 0;
      let all = [];
      let total = Infinity;
      while (offset < total) {
        const res = await endpoint.run({
          token,
          query: { offset, limit: 200 },
        });
        if (!res.result) break;
        const chunk = unwrapBankingList(res.data);
        all = all.concat(chunk);
        const reported = Number(res.data?.total_count);
        total =
          Number.isFinite(reported) && reported > 0 ? reported : all.length;
        offset += 200;
        if (chunk.length < 200) break;
      }
      setBanks(all);
    } catch (e) {
      message.warning(e.message);
    } finally {
      setLoadingBanks(false);
    }
  };

  const loadBranches = async (aliasCode, city) => {
    const { token } = user;
    if (!token || !aliasCode || !city) {
      setBranches([]);
      return;
    }
    setLoadingBranches(true);
    try {
      let offset = 0;
      let all = [];
      let total = Infinity;
      while (offset < total) {
        const res = await api.merchant.bankingBranches.run({
          token,
          query: {
            bank_alias_code: aliasCode,
            city_code: city,
            offset,
            limit: 100,
          },
        });
        if (!res.result) break;
        const chunk = unwrapBankingList(res.data);
        all = all.concat(chunk);
        const reported = Number(res.data?.total_count);
        total =
          Number.isFinite(reported) && reported > 0 ? reported : all.length;
        offset += 100;
        if (chunk.length < 100) break;
      }
      setBranches(all);
    } catch (e) {
      message.warning(e.message);
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  };

  const openBankPicker = async () => {
    setBankModalOpen(true);
    setBankKeyword("");
    if (!banks.length) {
      await loadAllBanks(bankAccountType);
    }
  };

  const onSelectBank = async (row) => {
    setSelectedBank(row);
    setBankModalOpen(false);
    const title = bankListTitle(row);
    form.setFieldsValue({
      account_bank: title,
      bank_name: title,
      bank_branch_id: undefined,
    });
    setBranches([]);
    if (cityCode && row.bank_alias_code) {
      await loadBranches(row.bank_alias_code, cityCode);
    }
  };

  const onSelectBranch = (branchId) => {
    const row = branches.find(
      (b) => String(b.bank_branch_id || b.branch_id) === String(branchId)
    );
    if (!row) return;
    form.setFieldsValue({
      bank_branch_id: String(row.bank_branch_id || row.branch_id || ""),
      bank_name: String(row.bank_branch_name || row.bank_name || ""),
    });
  };

  const runBankOcr = async (imageUrl) => {
    const { token } = user;
    if (!token || !imageUrl) return;
    setOcrLoading(true);
    try {
      const res = await api.merchant.ocrPrefill.run({
        token,
        data: { image_url: imageUrl, type: "bankcard" },
      });
      if (!res.result) {
        message.warning(res.message || i18n.t("financeAccount.ocrFailed"));
        return;
      }
      const patch = mapBankCardOcr(res.data);
      if (Object.keys(patch).length) {
        form.setFieldsValue(patch);
        message.success(i18n.t("financeAccount.ocrFilled"));
      }
    } catch (e) {
      message.warning(e.message || i18n.t("financeAccount.ocrFailed"));
    } finally {
      setOcrLoading(false);
    }
  };

  const uploadBankCard = useCallback(
    async ({ file, onError, onSuccess }) => {
      const { token } = user;
      if (!token) return onError("no token");
      try {
        const res = await api.common.uploadImages.run({
          token,
          uploadFiles: [{ file }],
          uploadType: "merchant-info",
        });
        if (res.result) {
          const uploaded = res.data[0];
          onSuccess(uploaded, file);
          if (uploaded?.url) await runBankOcr(uploaded.url);
        } else {
          onError(res.message);
        }
      } catch (e) {
        onError(e);
      }
    },
    [user]
  );

  const onFinish = useCallback(
    async (values) => {
      const { token } = user;
      if (!token) return;
      const {
        contact_name,
        contact_phone,
        bank_name,
        bank_account,
        bank_account_name,
        bank_account_type,
        account_bank,
        bank_address_code,
        bank_branch_id,
      } = values;
      const apiConfig = {
        token,
        data: {
          contact_name,
          contact_phone,
          bank_name,
          bank_account,
          bank_account_name,
          bank_account_type,
          account_bank,
          bank_address_code,
          bank_branch_id,
        },
        path: { id: refData.current?.id },
      };
      try {
        const res = await api.merchant.updateMerchantAccountInfo.run(apiConfig);
        if (res.result) {
          message.success(res.message);
          getData();
        } else {
          message.warning(res.message);
        }
      } catch (error) {
        message.error(error.message);
      }
    },
    [user, getData]
  );

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish}
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 10 }}
        initialValues={{ bank_account_type: "74" }}
      >
        <Form.Item
          name="contact_name"
          label={
            isAgentContact
              ? i18n.t("financeAccount.agentFullName")
              : i18n.t("financeAccount.ballanceInfoContact")
          }
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="contact_phone"
          label={
            isAgentContact
              ? i18n.t("financeAccount.agentContactPhoneHint")
              : i18n.t("financeAccount.ballanceInfoContactPhone")
          }
          extra={isAgentContact ? i18n.t("financeAccount.agentInfoHint") : null}
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="bank_account_type"
          label={i18n.t("financeAccount.bankInfoType")}
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          <Radio.Group
            onChange={() => {
              setBanks([]);
              setSelectedBank(null);
              setBranches([]);
              form.setFieldsValue({
                account_bank: undefined,
                bank_name: undefined,
                bank_branch_id: undefined,
              });
            }}
          >
            <Radio value="74">
              {i18n.t("financeAccount.bankInfoBankTypeCompany")}
            </Radio>
            <Radio value="75">
              {i18n.t("financeAccount.bankInfoBankTypePrivate")}
            </Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item label={i18n.t("financeAccount.bankCardImage")}>
          <Upload
            accept="image/*"
            maxCount={1}
            listType="picture-card"
            customRequest={uploadBankCard}
            getValueFromEvent={(e) => util.normFile(e)}
          >
            <Button icon={<UploadOutlined />}>
              {ocrLoading
                ? i18n.t("financeAccount.ocrRunning")
                : i18n.t("sales.upload")}
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item label={i18n.t("financeAccount.bankProvince")}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={i18n.t("financeAccount.bankProvince")}
            options={provinces.map((p) => ({
              value: p.province_code,
              label: p.province_name,
            }))}
            onChange={(code) => {
              setProvinceCode(code);
              setCityCode(null);
              setCities([]);
              setBranches([]);
              loadCities(code);
            }}
          />
        </Form.Item>

        <Form.Item label={i18n.t("financeAccount.bankCity")}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={i18n.t("financeAccount.bankCity")}
            disabled={!provinceCode}
            options={cities.map((c) => ({
              value: c.city_code,
              label: c.city_name,
            }))}
            onChange={(code) => {
              setCityCode(code);
              setBranches([]);
              form.setFieldsValue({
                bank_address_code: String(code || ""),
              });
              if (selectedBank?.bank_alias_code) {
                loadBranches(selectedBank.bank_alias_code, code);
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="account_bank"
          label={i18n.t("financeAccount.bankInfoName")}
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          <Input
            readOnly
            placeholder={i18n.t("financeAccount.selectBank")}
            addonAfter={
              <Button type="link" onClick={openBankPicker} style={{ padding: 0 }}>
                {i18n.t("financeAccount.selectBank")}
              </Button>
            }
          />
        </Form.Item>

        <Form.Item
          name="bank_name"
          label={i18n.t("financeAccount.bankInfoNameFull")}
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="bank_branch_id"
          label={i18n.t("financeAccount.bankInfoBranchId")}
          extra={
            loadingBranches
              ? i18n.t("financeAccount.loadingBranches")
              : i18n.t("financeAccount.bankBranchHint")
          }
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          {branches.length > 0 ? (
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={i18n.t("financeAccount.selectBranch")}
              onChange={onSelectBranch}
              options={branches.map((b) => ({
                value: String(b.bank_branch_id || b.branch_id),
                label: b.bank_branch_name || b.bank_name,
              }))}
            />
          ) : (
            <Input placeholder={i18n.t("financeAccount.bankInfoBranchId")} />
          )}
        </Form.Item>

        <Form.Item
          name="bank_account"
          label={i18n.t("financeAccount.bankInfoAccount")}
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="bank_account_name"
          label={i18n.t("financeAccount.bankInfoAccountName")}
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="bank_address_code"
          extra={i18n.t("financeAccount.bankAddressCodeHint")}
          label={i18n.t("financeAccount.bankInfoAddressCode")}
          rules={[
            { required: true, message: i18n.t("product.headerSeatchHint") },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 4 }}>
          <Button type="primary" htmlType="submit" size="large">
            {i18n.t("sales.submit")}
          </Button>
        </Form.Item>
      </Form>

      <Modal
        title={i18n.t("financeAccount.selectBank")}
        open={bankModalOpen}
        onCancel={() => setBankModalOpen(false)}
        footer={null}
        width={520}
      >
        <Input
          prefix={<SearchOutlined />}
          allowClear
          placeholder={i18n.t("financeAccount.searchBank")}
          value={bankKeyword}
          onChange={(e) => setBankKeyword(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        {loadingBanks ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <List
            style={{ maxHeight: 420, overflow: "auto" }}
            dataSource={filteredBanks}
            renderItem={(item) => (
              <List.Item
                style={{ cursor: "pointer" }}
                onClick={() => onSelectBank(item)}
              >
                <Space direction="vertical" size={0}>
                  <span>{bankListTitle(item)}</span>
                  {item.bank_alias &&
                  item.bank_alias !== bankListTitle(item) ? (
                    <span style={{ color: "#999", fontSize: 12 }}>
                      {item.bank_alias}
                    </span>
                  ) : null}
                </Space>
              </List.Item>
            )}
          />
        )}
      </Modal>
    </>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(AccountInfoSettingForm);
